import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';

// Import components
import { PasswordHints } from './PasswordHints';

// Import styles
import { InputContainer, InputWrapper, InputStyled, Label, Error, IconButton } from './styles';

// Import utilities
import { useHookFormField } from 'components/utilities';

// Import assets
import { ReactComponent as ShowIcon } from 'assets/icons/ic_password_show.svg';
import { ReactComponent as HideIcon } from 'assets/icons/ic_password_hide.svg';

// Import types
import type { PasswordFieldType, FormFieldProps } from 'types/views/form';

type Props = FormFieldProps<PasswordFieldType>;

export const PasswordInput = (props: Props) => {
	const { t } = useTranslation();
	const { field, fieldState, isError, isDisabled } = useHookFormField(props);
	const [showPassword, toggleShowPassword] = useToggle(false);
	const [showHints, setShowHint] = useState(false);

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		if (props.hints && e.relatedTarget?.tagName !== 'BUTTON' && e.relatedTarget?.tagName !== 'A') {
			setShowHint(false);
		}

		field.onBlur();
	};

	const showError = props.hints ? !showHints && isError : isError;

	return (
		<InputContainer>
			<InputWrapper>
				<InputStyled
					ref={field.ref}
					placeholder=" "
					value={field.value}
					id={field.name}
					name={field.name}
					onBlur={handleBlur}
					onChange={field.onChange}
					onFocus={setShowHint.bind(null, true)}
					disabled={isDisabled}
					isError={isError}
					type={showPassword ? 'text' : props.type}
					modifiers={props.modifiers}
					autoComplete={props.autoComplete}
				/>

				<Label modifiers={props.modifiers}>
					{t(props.label)} {props.required && '*'}
				</Label>

				<IconButton type="button" onClick={toggleShowPassword}>
					{showPassword ? <HideIcon /> : <ShowIcon />}
				</IconButton>
			</InputWrapper>

			{props.hints && showHints && <PasswordHints hints={props.hints} value={field.value} isError={isError} />}

			{showError && <Error modifiers={props.modifiers}>{t(fieldState.error?.message || '')}</Error>}
		</InputContainer>
	);
};
